import { useEffect, useState } from "react";
import { Close } from "../../Icons/Icons";
import { getCookie } from "../../reducer/utils";

const FOStatus = [
  "pending",
  "verification_in_progress",
  "verification_completed",
  "processing",
  "completed",
  "failed",
  "error",
];

const FutureOriginalCategoryFilter = ({
  showCategoryFilter,
  setShowCategoryFilter,
  onApplyFilter,
}) => {
  const defaultProviderFilter = {};
  const defaultCountryFilter = {};
  const defaultReleaseDateFilter = {
    gte: null,
    lte: null,
  };
  const defaultStatusesFilter = {};

  const countryNames = ["US", "AU", "GB", "CA", "DE"];

  const defaultFilter = {
    content_name: {
      like: "",
    },
    id: {
      in_list: [],
    },
    provider: {
      in_list: [],
    },
    country_code: {
      in_list: [],
    },
    release_date_time: defaultReleaseDateFilter,
    subscription: null,
    status: { in_list: [] },
  };

  const [filter, setFilter] = useState(defaultFilter);

  // "Hidden only" is a server-side filter: when checked, an `is_hidden` clause
  // is added to the /filter request payload.
  const [hiddenOnly, setHiddenOnly] = useState(false);

  const roles = getCookie("roles") || "";
  const checkRole = (role) => roles.includes(role);

  const [providerNames, setProviderNames] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState(
    defaultProviderFilter,
  );
  const [selectedStatuses, setSectedStatuses] = useState(defaultStatusesFilter);
  const [selectedCounries, setSelectedCounries] =
    useState(defaultCountryFilter);

  const baseURL = process.env.REACT_APP_BASE_URL;

  const clearFilter = () => {
    setFilter(defaultFilter);
    setSelectedProviders(defaultProviderFilter);
    setSelectedCounries(defaultCountryFilter);
    setSectedStatuses(defaultStatusesFilter);
    setHiddenOnly(false);
    onApplyFilter({});
  };

  const getProviderNames = async () => {
    try {
      const response = await fetch(
        `${baseURL}/internal/api/v4/utility/providers`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${getCookie("token")}`,
          },
        },
      );
      if (!response.ok) {
        // Keep the list empty on error (e.g. 403) so .map doesn't crash.
        setProviderNames([]);
        return;
      }
      const data = await response.json();
      setProviderNames(Array.isArray(data) ? data : []);
    } catch (error) {
      setProviderNames([]);
    }
  };

  const handleSelectProvider = (event) => {
    const { id, checked } = event.target;
    setSelectedProviders((prev) => ({ ...prev, [id]: checked }));
  };
  const handleSelectStatus = (event) => {
    const { id, checked } = event.target;
    setSectedStatuses((prev) => ({ ...prev, [id]: checked }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = { ...filter };
    if (hiddenOnly) {
      payload.is_hidden = { eq: true };
    }
    onApplyFilter(payload);
  };

  useEffect(() => {
    getProviderNames();
  }, []);

  useEffect(() => {
    const providerFilter = Object.keys(selectedProviders).filter(
      (provider) => selectedProviders[provider],
    );
    const newFilter = {
      ...filter,
      provider: {
        in_list: providerFilter,
      },
    };
    setFilter(newFilter);
  }, [selectedProviders]);

  const handleSelectCountry = (event) => {
    const { id, checked } = event.target;
    setSelectedCounries((prev) => ({ ...prev, [id]: checked }));
  };

  useEffect(() => {
    const countryFilter = Object.keys(selectedCounries).filter(
      (country_code) => selectedCounries[country_code],
    );
    const newFilter = {
      ...filter,
      country_code: {
        in_list: countryFilter,
      },
    };
    setFilter(newFilter);
  }, [selectedCounries]);

  useEffect(() => {
    const statusFilter = Object.keys(selectedStatuses).filter(
      (status) => selectedStatuses[status],
    );
    const newFilter = {
      ...filter,
      status: {
        in_list: statusFilter,
      },
    };
    setFilter(newFilter);
  }, [selectedStatuses]);


  return (
    <div
      className={`fixed left-0 top-0 z-50 h-screen w-screen ${showCategoryFilter ? "visible" : "hidden"}`}
    >
      <div
        className="absolute h-full w-full bg-gray-800 bg-opacity-25 backdrop-blur"
        onClick={() => setShowCategoryFilter(false)}
      ></div>
      <div
        className={`absolute mx-auto h-screen w-full max-w-md rounded-lg bg-white shadow-lg ${showCategoryFilter ? "right-0" : "-right-full"} top-0`}
      >
        <div className="flex items-center justify-between border-b p-5">
          <h2 className="text-lg font-bold font-semibold text-inherit">
            Filters
          </h2>
          <Close
            className="cursor-pointer"
            onClick={() => setShowCategoryFilter(false)}
          />
        </div>
        <form
          className="flex flex-col justify-between p-5"
          style={{ height: "calc(100vh - 70px)" }}
          onSubmit={handleSubmit}
        >
          <div className="max-h-full overflow-auto">
            {/* Content Name */}
            <p className="mb-2 block text-sm font-medium text-gray-900">
              Content Name
            </p>
            <div className="mb-5 flex items-start justify-start gap-3">
              <div className="w-full">
                <input
                  type="text"
                  value={filter.content_name?.like}
                  onChange={(e) =>
                    setFilter({
                      ...filter,
                      content_name: {
                        like: e.target.value,
                      },
                    })
                  }
                  className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                />
              </div>
            </div>

            {/* IDs */}
            <p className="mb-2 block text-sm font-medium text-gray-900">
              IDs (comma separated)
            </p>
            <div className="mb-5 flex items-start justify-start gap-3">
              <div className="w-full">
                <input
                  type="text"
                  value={filter.id?.in_list?.join(", ") || ""}
                  onChange={(e) => {
                    const ids = e.target.value
                      .split(",")
                      .map((id) => id.trim())
                      .filter((id) => id !== "");
                    setFilter({
                      ...filter,
                      id: {
                        in_list: ids,
                      },
                    });
                  }}
                  className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                  placeholder="e.g. 123, 456, 789"
                />
              </div>
            </div>

            {/* Release Date */}
            <p className="mb-2 block text-sm font-medium text-gray-900">
              Release Date
            </p>
            <div className="mb-5 flex items-start justify-start gap-3">
              <div className="w-full">
                <label className="mb-2 block text-sm font-medium text-gray-900">
                  From
                </label>
                <input
                  type="date"
                  value={filter.release_date_time?.gte?.split("T")[0] || ""}
                  onChange={(e) =>
                    setFilter({
                      ...filter,
                      release_date_time: {
                        gte: `${e.target.value}T00:00`,
                        lte: filter.release_date_time.lte,
                      },
                    })
                  }
                  className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                />
              </div>
              <div className="w-full">
                <label className="mb-2 block text-sm font-medium text-gray-900">
                  To
                </label>
                <input
                  type="date"
                  value={filter.release_date_time?.lte?.split("T")[0] || ""}
                  onChange={(e) =>
                    setFilter({
                      ...filter,
                      release_date_time: {
                        gte: filter.release_date_time.gte,
                        lte: `${e.target.value}T23:59`,
                      },
                    })
                  }
                  className="block w-full rounded-md border-gray-300 bg-gray-50 px-4 py-3 shadow-sm"
                />
              </div>
            </div>

            {/* Request Status */}
            <label className="mb-2 block text-sm font-medium text-gray-900">
              Provider
            </label>
            <div className="relative mb-5 block max-h-80 w-full overflow-auto rounded-md border border-gray-300 bg-gray-50 p-3 px-4 py-3 shadow-sm">
              {providerNames.map((provider, index) => (
                <div className="mb-4 flex items-center" key={index}>
                  <input
                    id={provider}
                    type="checkbox"
                    checked={selectedProviders[provider]}
                    onChange={handleSelectProvider}
                    className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-primary1"
                  />
                  <label
                    htmlFor={provider}
                    className="ms-2 text-sm font-medium text-gray-900"
                  >
                    {provider
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(" ")}
                  </label>
                </div>
              ))}
            </div>

            <label className="mb-2 block text-sm font-medium text-gray-900">
              FO Status
            </label>
            <div className="relative mb-5 block max-h-80 w-full overflow-auto rounded-md border border-gray-300 bg-gray-50 p-3 px-4 py-3 shadow-sm">
              {FOStatus.map((status, index) => (
                <div className="mb-4 flex items-center" key={index}>
                  <input
                    id={status}
                    type="checkbox"
                    checked={selectedStatuses[status]}
                    onChange={handleSelectStatus}
                    className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-primary1"
                  />
                  <label
                    htmlFor={status}
                    className="ms-2 text-sm font-medium text-gray-900"
                  >
                    {status
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(" ")}
                  </label>
                </div>
              ))}
            </div>

            <label className="mb-2 block text-sm font-medium text-gray-900">
              Country
            </label>
            <div className="flex gap-5">
              {countryNames.map((country_code, index) => (
                <p
                  className="mb-2 flex cursor-pointer items-center pl-1 text-sm font-medium text-gray-900"
                  key={index}
                >
                  <input
                    id={country_code}
                    type="checkbox"
                    checked={selectedCounries[country_code]}
                    onChange={handleSelectCountry}
                    className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-primary1"
                  />
                  <label
                    htmlFor={country_code}
                    className="ms-2 text-sm font-medium text-gray-900"
                  >
                    {country_code
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(" ")}
                  </label>
                </p>
              ))}
            </div>

            {checkRole("internal") && (
              <>
                <label className="mb-2 mt-5 block text-sm font-medium text-gray-900">
                  Content
                </label>
                <div className="mb-5 flex flex-col gap-3">
                  <p className="flex cursor-pointer items-center pl-1 text-sm font-medium text-gray-900">
                    <input
                      id="hidden_only"
                      type="checkbox"
                      checked={hiddenOnly}
                      onChange={(e) => setHiddenOnly(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-primary1"
                    />
                    <label
                      htmlFor="hidden_only"
                      className="ms-2 cursor-pointer text-sm font-medium text-gray-900"
                    >
                      Hidden only
                    </label>
                  </p>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <button
              type="submit"
              className="w-full rounded-lg bg-primary1 px-3 py-2 text-white"
            >
              Apply filter
            </button>
            <button
              type="reset"
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-900"
              onClick={() => clearFilter()}
            >
              Clear all
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FutureOriginalCategoryFilter;
